import {
  TEXT_AUTOWRAP_THRESHOLD,
  getGridPoint,
  getFontString,
} from "@excalidraw/common";

import type {
  AppState,
  NormalizedZoomValue,
  NullableGridSize,
  PointerDownState,
} from "@excalidraw/excalidraw/types";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { updateBoundElements } from "./binding";
import { getCommonBounds } from "./bounds";
import { getAllNestedFrameChildren, validateAndFixNestedFrames } from "./frame";
import { getPerfectElementSize } from "./sizeHelpers";
import { getBoundTextElement } from "./textElement";
import { getMinTextElementWidth } from "./textMeasurements";
import {
  isArrowElement,
  isElbowArrow,
  isFrameLikeElement,
  isImageElement,
  isTextElement,
} from "./typeChecks";

import type { Scene } from "./Scene";

import type { Bounds } from "./bounds";
import type { ExcalidrawElement, ExcalidrawFrameElement } from "./types";

export const dragSelectedElements = (
  pointerDownState: PointerDownState,
  _selectedElements: NonDeletedExcalidrawElement[],
  offset: { x: number; y: number },
  scene: Scene,
  snapOffset: {
    x: number;
    y: number;
  },
  gridSize: NullableGridSize,
) => {
  if (
    _selectedElements.length === 1 &&
    isElbowArrow(_selectedElements[0]) &&
    (_selectedElements[0].startBinding || _selectedElements[0].endBinding)
  ) {
    return;
  }

  const selectedElements = _selectedElements.filter((element) => {
    if (isElbowArrow(element) && element.startBinding && element.endBinding) {
      const startElement = _selectedElements.find(
        (el) => el.id === element.startBinding?.elementId,
      );
      const endElement = _selectedElements.find(
        (el) => el.id === element.endBinding?.elementId,
      );

      return startElement && endElement;
    }

    return true;
  });

  // we do not want a frame and its elements to be selected at the same time
  // but when it happens (due to some bug), we want to avoid updating element
  // in the frame twice, hence the use of set
  const elementsToUpdate = new Set<NonDeletedExcalidrawElement>(
    selectedElements,
  );
  const frames = selectedElements
    .filter((e) => isFrameLikeElement(e))
    .map((f) => f.id);

  if (frames.length > 0) {
    // Check if we're dragging elements within an auto layout frame for reordering
    // In this case, we don't want to add nested children as it breaks reordering detection
    const isAutoLayoutReordering = selectedElements.some((element) => {
      if (!element.frameId) return false;
      const parentFrame = scene.getNonDeletedElementsMap().get(element.frameId);
      return parentFrame && 
             isFrameLikeElement(parentFrame) && 
             (parentFrame as ExcalidrawFrameElement).autoLayout?.enabled;
    });

    if (!isAutoLayoutReordering) {
    // Add all nested frame children recursively for each selected frame
    for (const frameId of frames) {
      const allNestedChildren = getAllNestedFrameChildren(
        scene.getNonDeletedElementsMap(),
        frameId,
      );

      for (const child of allNestedChildren) {
        if (!child.isDeleted) {
          elementsToUpdate.add(child);
          }
        }
      }
    }
  }

  // Ensure original snapshot exists for every element we are going to mutate
  for (const element of elementsToUpdate) {
    if (!pointerDownState.originalElements.has(element.id)) {
      // Clone the element's current state as its original for consistent offset calculations
      pointerDownState.originalElements.set(element.id, { ...element });
    }
  }

  const origElements: ExcalidrawElement[] = [];

  for (const element of elementsToUpdate) {
    const origElement = pointerDownState.originalElements.get(element.id);
    if (!origElement) {
      // Should not happen due to the above guard but skip just in case
      continue;
    }
    origElements.push(origElement);
  }

  // If for some reason no original elements collected, bail out
  if (origElements.length === 0) {
    return;
  }

  const adjustedOffset = calculateOffset(
    getCommonBounds(origElements),
    offset,
    snapOffset,
    gridSize,
  );

  const elementsMap = scene.getNonDeletedElementsMap();

  // Check if we need to constrain movement within auto-layout frames
  let constrainedOffset = { ...adjustedOffset };

  // Group elements by their frame to check constraints
  const elementsByFrame = new Map<
    string | null,
    NonDeletedExcalidrawElement[]
  >();
  for (const element of elementsToUpdate) {
    const frameId = element.frameId;
    if (!elementsByFrame.has(frameId)) {
      elementsByFrame.set(frameId, []);
    }
    elementsByFrame.get(frameId)!.push(element);
  }

  // Check each frame's constraints
  for (const [frameId, frameElements] of elementsByFrame) {
    if (frameId) {
      const frame = elementsMap.get(frameId);
      // Skip constraint for auto-layout frames - let auto-layout handle positioning
      if (
        frame &&
        isFrameLikeElement(frame) &&
        // Skip constraint if the frame itself is part of the drag selection
        !elementsToUpdate.has(frame) &&
        !(frame as ExcalidrawFrameElement).autoLayout?.enabled
      ) {
        // This is a regular frame, constrain movement within its bounds
        const frameBounds = getCommonBounds([frame]);
        const [frameX1, frameY1, frameX2, frameY2] = frameBounds;

        // Get the bounds of elements being dragged within this frame
        const origFrameElements = frameElements.map(
          (el) => pointerDownState.originalElements.get(el.id)!,
        );
        const origBounds = getCommonBounds(origFrameElements);
        const [origX1, origY1, origX2, origY2] = origBounds;

        // Calculate new bounds after applying offset
        const newX1 = origX1 + adjustedOffset.x;
        const newY1 = origY1 + adjustedOffset.y;
        const newX2 = origX2 + adjustedOffset.x;
        const newY2 = origY2 + adjustedOffset.y;

        // Calculate constraints
        let constrainedX = adjustedOffset.x;
        let constrainedY = adjustedOffset.y;

        // Constrain left edge
        if (newX1 < frameX1) {
          constrainedX = frameX1 - origX1;
        }
        // Constrain right edge
        else if (newX2 > frameX2) {
          constrainedX = frameX2 - origX2;
        }

        // Constrain top edge
        if (newY1 < frameY1) {
          constrainedY = frameY1 - origY1;
        }
        // Constrain bottom edge
        else if (newY2 > frameY2) {
          constrainedY = frameY2 - origY2;
        }

        // Apply the most restrictive constraint to all elements
        if (
          constrainedX !== adjustedOffset.x ||
          constrainedY !== adjustedOffset.y
        ) {
          constrainedOffset = {
            x: constrainedX,
            y: constrainedY,
          };
        }
      }
    }
  }

  for (const element of elementsToUpdate) {
    const origElement = pointerDownState.originalElements.get(element.id)!;
    scene.mutateElement(
      element,
      {
        x: origElement.x + constrainedOffset.x,
        y: origElement.y + constrainedOffset.y,
      },
      { informMutation: false, isDragging: true },
    );
  }
  
  // After all elements have been moved, validate and fix nested frames
  // to ensure child frames remain within their parent frames
  // Skip this for auto-layout frames as they handle positioning themselves
  const elementsToValidate = scene.getNonDeletedElements().filter(el => {
    if (!isFrameLikeElement(el) || !el.frameId) return true;
    const parentFrame = elementsMap.get(el.frameId);
    return !(parentFrame && 
             isFrameLikeElement(parentFrame) && 
             (parentFrame as any).autoLayout?.enabled);
  });
  
  if (elementsToValidate.length > 0) {
    validateAndFixNestedFrames(elementsToValidate, scene);
  }

  // handle bound text elements
  for (const element of elementsToUpdate) {
    if (!isArrowElement(element)) {
      const textElement = getBoundTextElement(element, elementsMap);
      if (
        textElement &&
        elementsToUpdate.has(textElement) &&
        pointerDownState.originalElements.has(textElement.id)
      ) {
        const origTextElement = pointerDownState.originalElements.get(
          textElement.id,
        )!;
        scene.mutateElement(
          textElement,
          {
            x: origTextElement.x + constrainedOffset.x,
            y: origTextElement.y + constrainedOffset.y,
          },
          {
            informMutation: false,
            isDragging: true,
          },
        );
      }
    }
  }

  // Update bound elements after all elements have been moved
  for (const element of elementsToUpdate) {
    if (!isArrowElement(element)) {
      updateBoundElements(element, scene, {
        simultaneouslyUpdated: Array.from(elementsToUpdate),
      });
    }
  }

  // Trigger a single update after all mutations are done
  scene.triggerUpdate();
};

const calculateOffset = (
  commonBounds: Bounds,
  dragOffset: { x: number; y: number },
  snapOffset: { x: number; y: number },
  gridSize: NullableGridSize,
): { x: number; y: number } => {
  const [x, y] = commonBounds;
  let nextX = x + dragOffset.x + snapOffset.x;
  let nextY = y + dragOffset.y + snapOffset.y;

  if (snapOffset.x === 0 || snapOffset.y === 0) {
    const [nextGridX, nextGridY] = getGridPoint(
      x + dragOffset.x,
      y + dragOffset.y,
      gridSize,
    );

    if (snapOffset.x === 0) {
      nextX = nextGridX;
    }

    if (snapOffset.y === 0) {
      nextY = nextGridY;
    }
  }
  return {
    x: nextX - x,
    y: nextY - y,
  };
};

export const getDragOffsetXY = (
  selectedElements: NonDeletedExcalidrawElement[],
  x: number,
  y: number,
): [number, number] => {
  const [x1, y1] = getCommonBounds(selectedElements);
  return [x - x1, y - y1];
};

export const dragNewElement = ({
  newElement,
  elementType,
  originX,
  originY,
  x,
  y,
  width,
  height,
  shouldMaintainAspectRatio,
  shouldResizeFromCenter,
  zoom,
  scene,
  widthAspectRatio = null,
  originOffset = null,
  informMutation = true,
}: {
  newElement: NonDeletedExcalidrawElement;
  elementType: AppState["activeTool"]["type"];
  originX: number;
  originY: number;
  x: number;
  y: number;
  width: number;
  height: number;
  shouldMaintainAspectRatio: boolean;
  shouldResizeFromCenter: boolean;
  zoom: NormalizedZoomValue;
  scene: Scene;
  /** whether to keep given aspect ratio when `isResizeWithSidesSameLength` is
      true */
  widthAspectRatio?: number | null;
  originOffset?: {
    x: number;
    y: number;
  } | null;
  informMutation?: boolean;
}) => {
  if (shouldMaintainAspectRatio && newElement.type !== "selection") {
    if (widthAspectRatio) {
      height = width / widthAspectRatio;
    } else {
      // Depending on where the cursor is at (x, y) relative to where the starting point is
      // (originX, originY), we use ONLY width or height to control size increase.
      // This allows the cursor to always "stick" to one of the sides of the bounding box.
      if (Math.abs(y - originY) > Math.abs(x - originX)) {
        ({ width, height } = getPerfectElementSize(
          elementType,
          height,
          x < originX ? -width : width,
        ));
      } else {
        ({ width, height } = getPerfectElementSize(
          elementType,
          width,
          y < originY ? -height : height,
        ));
      }

      if (height < 0) {
        height = -height;
      }
    }
  }

  let newX = x < originX ? originX - width : originX;
  let newY = y < originY ? originY - height : originY;

  if (shouldResizeFromCenter) {
    width += width;
    height += height;
    newX = originX - width / 2;
    newY = originY - height / 2;
  }

  let textAutoResize = null;

  if (isTextElement(newElement)) {
    height = newElement.height;
    const minWidth = getMinTextElementWidth(
      getFontString({
        fontSize: newElement.fontSize,
        fontFamily: newElement.fontFamily,
      }),
      newElement.lineHeight,
    );
    width = Math.max(width, minWidth);

    if (Math.abs(x - originX) > TEXT_AUTOWRAP_THRESHOLD / zoom) {
      textAutoResize = {
        autoResize: false,
      };
    }

    newY = originY;
    if (shouldResizeFromCenter) {
      newX = originX - width / 2;
    }
  }

  if (width !== 0 && height !== 0) {
    let imageInitialDimension = null;
    if (isImageElement(newElement)) {
      imageInitialDimension = {
        initialWidth: width,
        initialHeight: height,
      };
    }

    scene.mutateElement(
      newElement,
      {
        x: newX + (originOffset?.x ?? 0),
        y: newY + (originOffset?.y ?? 0),
        width,
        height,
        ...textAutoResize,
        ...imageInitialDimension,
      },
      { informMutation, isDragging: false },
    );
  }
};
